import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { NEWS_POSTS, getNewsPost, type NewsBlock } from '@/data/newsData';
import s from './NewsDetail.module.scss';

// 뉴스 상세 — 블록 기반(각 글 캡처를 읽고 재해석한 blocks 렌더). 디자인 방법론:
//   다크 히어로 · 넘버링 섹션 · 메트릭/피처 카드 · 라이트/다크 콜아웃 · 풀폭 다크 CONCLUSION.
//   이미지 uncut(테두리 없이) · 텍스트 EVS 원문 verbatim(강조 span만 디자인). [[evs-news-architecture]]
const html = (h: string) => ({ dangerouslySetInnerHTML: { __html: h } });

function Block({ b }: { b: NewsBlock }) {
  switch (b.t) {
    case 'image':
      return <figure className={s.figFull}><img src={b.src} alt={b.alt} loading="lazy" /></figure>;
    case 'p':
      return <p className={b.strong ? s.pStrong : undefined} {...html(b.html)} />;
    case 'quoteLight':
      return <div className={s.quoteLight} {...html(b.html)} />;
    case 'quoteDark':
      return <div className={s.quoteDark} {...html(b.html)} />;
    case 'section':
      return (
        <>
          <div className={s.sec}><i className={`ti ti-${b.icon}`} aria-hidden /><span className={s.n}>{b.n} · {b.en}</span></div>
          <h2 className={s.h2}>{b.title}</h2>
        </>
      );
    case 'metrics':
      return (
        <div className={s.metrics}>
          {b.items.map((m, i) => (
            <div key={i} className={`${s.metric} ${m.warn ? s.warn : ''}`}>
              <div className={s.mk}>{m.k}</div>
              <div className={s.mv}>{m.v}{m.unit && <small>{m.unit}</small>}</div>
              <div className={s.md}>{m.d}</div>
            </div>
          ))}
        </div>
      );
    case 'cards':
      return (
        <div className={s.cards}>
          {b.items.map((c, i) => (
            <div key={i} className={s.fcard}>
              <i className={`ti ti-${c.icon}`} aria-hidden />
              <h3>{c.title}</h3><p>{c.body}</p>
            </div>
          ))}
        </div>
      );
    case 'risk':
      return (
        <div className={s.risk}>
          {b.items.map((r, i) => (
            <div key={i}><b>{r.title}</b><span>{r.body}</span></div>
          ))}
        </div>
      );
    case 'conclude':
      return (
        <section className={s.conclude}>
          <div className={s.col}>
            <div className={s.sec}><i className={`ti ti-${b.icon}`} aria-hidden /><span className={s.n}>{b.n} · {b.en}</span></div>
            <h2 className={s.h2}>{b.title}</h2>
            {b.body.map((x, i) => x.k === 'inbox'
              ? <div key={i} className={s.inbox} {...html(x.html)} />
              : <p key={i} {...html(x.html)} />)}
          </div>
        </section>
      );
    default:
      return null;
  }
}

export default function NewsDetail() {
  const { slug = '' } = useParams();
  const post = getNewsPost(slug);
  // 스크롤 최상단 리셋은 App의 <ScrollToTop/> 이 라우트 공통으로 처리(Lenis immediate+다음프레임 확정).
  //   여기서 window.scrollTo(0,0)를 또 부르면 CSS smooth와 경쟁해 중간에 멈추는 원인이 되어 제거함(2026-09-08).

  // 심플·세련된 진입 로더(2026-09-08 사용자 요청).
  //   데이터는 동기 렌더라 즉시 뜨지만, 고해상 이미지(≈966px) 로딩엔 시간이 걸림.
  //   → slug 변경마다 로더 표시 후 "히어로(cover) 이미지 로드 완료" 또는 최대 1.1s 시점에 종료.
  //   최소 300ms 유지(깜빡임 방지). performance.now() 사용(Date.now 회피).
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const start = performance.now();
    let done = false;
    const finish = () => {
      if (done) return; done = true;
      const wait = Math.max(0, 300 - (performance.now() - start));
      window.setTimeout(() => setLoading(false), wait);
    };
    const cap = window.setTimeout(finish, 1100); // 이미지가 느려도 1.1s 후 종료
    if (post?.cover) {
      const im = new Image();
      im.onload = finish; im.onerror = finish; im.src = post.cover;
      if (im.complete) finish();
    } else finish();
    return () => window.clearTimeout(cap);
  }, [slug]);

  if (!post) {
    return (
      <main className={s.notfound}>
        <p>글을 찾을 수 없습니다.</p>
        <Link to="/news" className={s.back}><i className="ti ti-list" aria-hidden /> 뉴스 &amp; 소식 목록으로</Link>
      </main>
    );
  }

  // prev/next는 같은 도메인(안전/환경) 안에서만 이동(뉴스 탭과 정합, 2026-09-08).
  const domainPosts = NEWS_POSTS.filter((p) => p.domain === post.domain);
  const idx = domainPosts.findIndex((p) => p.slug === slug);
  const prev = idx > 0 ? domainPosts[idx - 1] : null;
  const next = idx < domainPosts.length - 1 ? domainPosts[idx + 1] : null;
  const domainLabel = post.domain === 'safety' ? '안전' : '환경';

  return (
    <article className={post.domain === 'environment' ? s.env : undefined}>
      {/* 진입 로더 — 네이비 오버레이 + 시안 링(환경 글은 그린). loading 해제 시 페이드아웃(노드는 유지) */}
      <div className={`${s.loader} ${loading ? '' : s.loaderDone}`} aria-hidden>
        <span className={s.ring} />
        <span className={s.loaderBrand}>EVS</span>
      </div>
      {/* 안전/환경 도메인 스위처 — 상세 페이지에선 '실시간 상담' 버튼 위에 플로팅(2026-09-08 사용자).
          두 탭 동일 크기. 현재 글 도메인 활성. 클릭 시 해당 도메인 목록(/news?tab=…)으로 이동. */}
      <div className={s.domainFloat} role="tablist" aria-label="뉴스 카테고리">
        <Link to="/news?tab=safety" role="tab" aria-selected={post.domain === 'safety'}
          className={`${s.dfTab} ${post.domain === 'safety' ? s.on : ''}`}>안전</Link>
        <Link to="/news?tab=environment" role="tab" aria-selected={post.domain === 'environment'}
          className={`${s.dfTab} ${post.domain === 'environment' ? s.on : ''}`}>환경</Link>
      </div>

      <header className={s.hero}>
        <div className={s.col}>
          {/* [2026-10-11 사용자] 전수 조사에서 뉴스 상세도 브레드크럼 누락 → 추가(고객지원 › 뉴스·소식). */}
          <nav className={s.crumb} aria-label="현재 위치">
            <a href="#" onClick={(e) => e.preventDefault()}>HOME</a>
            <span className={s.csep} aria-hidden>›</span>
            <a href="#" onClick={(e) => e.preventDefault()}>고객지원</a>
            <span className={s.csep} aria-hidden>›</span>
            <span className={s.ccur} aria-current="page">뉴스·소식</span>
          </nav>
          <div className={s.eyebrow}><span className={s.bar} />{domainLabel} · {post.category}</div>
          <h1 className={s.title}>{post.title}</h1>
          <p className={s.lead}>{post.lead}</p>
          <div className={s.meta}>
            {/* 'N분 분량'(readMin) 표기 제거(2026-09-08 사용자) — 임의 추정값이라 근거 불명확·불필요.
                데이터 필드(post.readMin)는 존치하되 화면 표시만 제거(추후 필요 시 복원 용이). */}
            <b>EVS</b><span>·</span><span>{post.date}</span>
          </div>
        </div>
      </header>

      <main className={s.body}>
        {post.ready && post.blocks
          ? post.blocks.map((b, i) =>
              (b.t === 'image' || b.t === 'conclude')
                ? <Block key={i} b={b} />
                : <div key={i} className={s.col}><Block b={b} /></div>)
          : <div className={s.col}><p className={s.soon}>이 글의 상세 내용은 준비 중입니다. 곧 업데이트됩니다.</p></div>}

        {/* 제품 CTA — 도메인별(안전=체감온도계 / 환경=대기배출 IoT 게이트웨이) */}
        <div className={s.cta}>
          <div className={s.ctaT}>
            {post.domain === 'environment' ? (
              <>
                <div className={s.ctaKick}>EVS ENVIRONMENT IoT</div>
                <b>대기배출 IoT 측정기기 · IoT GateWay (EVS-G200)</b>
                <small>방지시설 실시간 원격 감시 · 그린링크(GreenLink) 자동 전송 · 원스톱 설치</small>
              </>
            ) : (
              <>
                <div className={s.ctaKick}>EVS SAFETY IoT</div>
                <b>AI 온열질환 IoT 체감온도계</b>
                <small>실시간 WBGT 관제로 법정 기록 자동 저장 · SaaS 통합관제</small>
              </>
            )}
          </div>
          <a href="#" onClick={(e) => e.preventDefault()}>제품 자세히 보기 →</a>
        </div>

        <div className={s.col}>
          <div className={s.nav2}>
            {prev
              ? <Link to={`/news/${prev.slug}`}><div className={s.k}>‹ 이전 글</div><div className={s.v}>{prev.title}</div></Link>
              : <span />}
            {next
              ? <Link to={`/news/${next.slug}`} className={s.next}><div className={s.k}>다음 글 ›</div><div className={s.v}>{next.title}</div></Link>
              : <span />}
          </div>
          <div className={s.backWrap}><Link to="/news" className={s.back}><i className="ti ti-list" aria-hidden /> 뉴스 &amp; 소식 전체 목록으로</Link></div>
        </div>
      </main>
    </article>
  );
}
